import {type FormEventHandler, useState} from 'react';
import {useIntl} from 'react-intl';

import {type Editor, type JSONContent} from '@tiptap/react';
import {Button, BUTTON_SIZE, BUTTON_VARIANT, Input, FormError} from '@time-webkit/all/atoms';
import {sendToStatist} from 'packages/statist';

import {hasProtocol, prepareForm} from '../utils';

import styles from './link-popover-form.module.css';

type LinkPopoverFormProps = {
    editor?: Editor;
    onTogglePopover: () => void;
}

const LINK_INPUT_ERROR_PLACEHOLDER = 'https://example.com';
export const LinkPopoverForm = ({editor, onTogglePopover}: LinkPopoverFormProps) => {
    const [linkValue, setLinkValue] = useState('');
    const [textValue, setTextValue] = useState('');
    const [error, setError] = useState('');
    const {formatMessage} = useIntl();

    const handleChangeLinkValue = (newLinkValue: string) => {
        setLinkValue(newLinkValue);

        if (error) {
            setError('');
        }
    };

    const handleSubmit: FormEventHandler = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!editor) {
            return;
        }

        if (linkValue.trim().length === 0) {
            return setError(formatMessage({
                id: 'editor.link-popover.link-field.error.empty',
                defaultMessage: 'Enter the link',
            }));
        }

        const {url, text} = prepareForm(linkValue, textValue);

        const marks: JSONContent['marks'] = [{type: 'link', attrs: {href: url}}];

        ['bold', 'italic', 'strike'].forEach((mark) => {
            if (editor.isActive(mark)) {
                marks.push({type: mark});
            }
        });

        const content: JSONContent[] = [{type: 'text', text, marks}];

        const startSpaces = textValue.match(/^\s+/)?.[0];
        const endSpaces = textValue.match(/\s+$/)?.[0];
        const hasText = textValue.trim().length > 0;

        if (startSpaces && hasText) {
            content.unshift({type: 'text', text: startSpaces});
        }

        if (endSpaces && hasText) {
            content.push({type: 'text', text: endSpaces});
        }

        sendToStatist('editor.link.apply', {
            highlight: false,
            insert: false,
            text: hasText,
            protocol: hasProtocol(linkValue.trim()),
        });

        editor
            .chain()
            .focus()
            .insertContent(content)
            .run();
    };

    const hasError = Boolean(error);

    return (
        <form className={styles.linkPopoverForm} onSubmit={handleSubmit}>
            <div className={styles.inputWrapper}>
                <Input
                    value={textValue}
                    onChange={setTextValue}
                    className={styles.textInput}
                    label={formatMessage({id: 'editor.link-popover.text-field.label', defaultMessage: 'Text'})}
                />
                <Input
                    error={hasError}
                    value={linkValue}
                    onChange={handleChangeLinkValue}
                    className={styles.linkInput}
                    label={formatMessage({id: 'editor.link-popover.link-field.label', defaultMessage: 'Link'})}
                    placeholder={hasError ? LINK_INPUT_ERROR_PLACEHOLDER : ''}
                />
                <FormError show={hasError} message={error} className={styles.linkError}/>
            </div>
            <div className={styles.buttonsWrapper}>
                <Button
                    size={BUTTON_SIZE.XSMALL}
                    variant={BUTTON_VARIANT.FLAT}
                    onPress={onTogglePopover}
                >
                    {formatMessage({id: 'editor.link-popover.buttons.cancel', defaultMessage: 'Cancel'})}
                </Button>
                <Button
                    size={BUTTON_SIZE.XSMALL}
                    variant={BUTTON_VARIANT.SECONDARY}
                    type='submit'
                >
                    {formatMessage({id: 'editor.link-popover.buttons.add', defaultMessage: 'Add'})}
                </Button>
            </div>
        </form>
    );
};
